/**
 * =============================================================================
 * Componente: LinkCard
 * Versão: 1.0.0 (Fase 3 — Bloco 3B, parte 2)
 * Descrição: Card visual de um link de afiliado com:
 *   - URL pública renderizada (curta via /r/<short_code>)
 *   - Botão "Copiar"
 *   - Placeholders para QR Code e share WhatsApp (Fase 4)
 *
 * A URL pública usa o short_code (mais amigável); fallback para slug.
 * =============================================================================
 */
import { useState } from 'react';
import { Copy, Check, QrCode, Share2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAffiliateLinkActions } from '@/hooks/useAffiliateLinkActions';
import { QrCodeDialog } from '@/components/affiliate/QrCodeDialog';

export interface LinkCardData {
  id: string;
  slug: string;
  short_code: string | null;
  destination_url: string;
  is_active: boolean;
  campaign_name?: string | null;
}

interface LinkCardProps {
  link: LinkCardData;
  /** Origem pública (ex.: https://uaizychat.lovable.app). Default: window.location.origin. */
  publicOrigin?: string;
}

/**
 * Constrói a URL pública de divulgação do link.
 * Padrão: <origin>/r/<short_code|slug>
 */
function buildPublicUrl(link: LinkCardData, origin: string): string {
  const code = link.short_code ?? link.slug;
  return `${origin.replace(/\/$/, '')}/r/${code}`;
}

export function LinkCard({ link, publicOrigin }: LinkCardProps) {
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const { shareWhatsapp, isSharingWhatsapp } = useAffiliateLinkActions();

  const origin = publicOrigin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const publicUrl = buildPublicUrl(link, origin);

  const handleShareWhatsapp = async () => {
    try {
      const res = await shareWhatsapp({ linkId: link.id });
      window.open(res.whatsapp_url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Não foi possível abrir o WhatsApp');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      toast.error('Não foi possível copiar o link');
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="space-y-3 p-4 min-w-0">
        {/* Cabeçalho: campanha + status */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium text-foreground truncate">
              {link.campaign_name ?? 'Link direto'}
            </div>
            <div className="text-xs text-muted-foreground truncate" title={link.destination_url}>
              {link.destination_url}
            </div>
          </div>
          {/*
            Status no padrão "dot colorido + texto preto" — mesmo padrão da
            tela de Indicações. Sem fundo/badge colorido para evitar visual
            "AI-generated".
          */}
          <span className="inline-flex shrink-0 items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--aff-foreground))]">
            <span
              aria-hidden
              className={cn(
                'h-2 w-2 rounded-full shrink-0',
                link.is_active
                  ? 'bg-[hsl(var(--aff-success))]'
                  : 'bg-[hsl(var(--aff-muted-foreground))]',
              )}
            />
            {link.is_active ? 'Ativo' : 'Inativo'}
          </span>
        </div>

        {/* URL pública */}
        <div className="flex items-center gap-2 min-w-0 rounded-md border border-border bg-muted/40 px-3 py-2">
          <code className="flex-1 truncate text-xs font-mono text-foreground">
            {publicUrl}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copiar link"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-black transition-colors hover:bg-neutral-50"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>

        {/*
          Ações secundárias — visual editorial monocromático (preto/branco),
          alinhado ao redesign dos toasts. Sem cores de marca nem gradientes.
        */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setQrOpen(true)}
            title="Gerar QR Code do link"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12px] font-medium text-black transition-colors hover:bg-neutral-50"
          >
            <QrCode className="h-3.5 w-3.5" />
            QR Code
          </button>
          <button
            type="button"
            onClick={handleShareWhatsapp}
            disabled={isSharingWhatsapp}
            title="Compartilhar no WhatsApp"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12px] font-medium text-black transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSharingWhatsapp ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Share2 className="h-3.5 w-3.5" />
            )}
            Compartilhar
          </button>
        </div>
      </CardContent>

      <QrCodeDialog
        open={qrOpen}
        onOpenChange={setQrOpen}
        linkId={link.id}
        downloadFileName={link.short_code ?? link.slug}
      />
    </Card>
  );
}
